import type { InputRef } from 'antd/es/input';
import type { ChangeEvent, Dispatch } from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';

import { countCharacter } from 'utils/char-counter';

type AntMaskProps = {
  /**
   * Validates the text in the input field
   */
  validator: RegExp;
  /**
   * Replaces the matches of the regex with an empty string before calling `onChange`
   */
  replacer: RegExp;
  /**
   * Separator character used in formatting. Used to calculate/update caret.
   */
  separator: string;
  /**
   * Formatter for formatting raw input to formatted input
   */
  formatter: (input: string) => string;
  /**
   * Called on input update. Will dispatch cleaned output string, not the formatted value.
   */
  onChange?: Dispatch<string>;
  /**
   * Clean value (not formatted). Used for controlled inputs.
   */
  value?: string;
};

export const useAntMask = ({
  validator,
  replacer,
  separator,
  formatter,
  onChange: onChangeCallback = () => {},
  value
}: AntMaskProps) => {
  const ref = useRef<InputRef>(null);

  const [input, setInput] = useState(value ? formatter(value) : undefined);

  useEffect(() => {
    setInput(value ? formatter(value) : '');
  }, [value]);

  useLayoutEffect(() => {
    const unmaskedValue = input?.replaceAll(replacer, '');
    if (unmaskedValue !== undefined) {
      onChangeCallback(unmaskedValue);
    }
  }, [input]);

  const moveCaretRelativeTo = useCallback((caret: number) => {
    return (offset: number) => {
      const element = ref.current;
      /**
       * Custom handling of caret
       * Idea from: https://stackoverflow.com/a/49648061
       * Callback is wrapped in requestAnimationFrame
       * to ensure callback is called before next re-render
       */
      window.requestAnimationFrame(() => {
        element?.setSelectionRange(caret + offset, caret + offset, 'forward');
      });
    };
  }, []);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value: inputValue, selectionStart } = e.target;
      const offsetCaret = moveCaretRelativeTo(selectionStart ?? 0);

      // Allow empty string
      if (inputValue === '') {
        return setInput('');
      }

      if (validator.test(inputValue) === false) {
        // Move caret 1 to the left, since no character was added, then early return
        return offsetCaret(-1);
      }

      // Format input value - removes commas for formatter
      const formattedValue = formatter(inputValue.replaceAll(separator, ''));

      // Find difference in number of commas to calculate how far caret should move
      const countCommas = countCharacter(separator);
      const caretDifference =
        countCommas(formattedValue) - countCommas(inputValue);

      offsetCaret(caretDifference);

      setInput(formattedValue);
    },
    [moveCaretRelativeTo, setInput, replacer, validator]
  );

  return {
    mask: {
      ref,
      onChange,
      value: input ?? ''
    },
    setFormattedInput: setInput
  };
};
